import type { CSSProperties, MouseEvent, RefObject } from 'react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { plural, t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Portal } from '@radix-ui/react-portal'
import { useVirtualizer } from '@tanstack/react-virtual'

import { CircularProgress } from '@jotta/ui/CircularProgress'
import { CopyButton } from '@jotta/ui/CopyButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@jotta/ui/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@jotta/ui/DropdownMenu'
import Spinner from '@jotta/ui/Spinner'
import SvgBoldCheck from '@jotta/ui/SvgBoldCheck'
import SvgCaretDown from '@jotta/ui/SvgCaretDown'
import SvgCaretUp from '@jotta/ui/SvgCaretUp'
import SvgExplamationMark from '@jotta/ui/SvgExclamationMark'
import SvgFile from '@jotta/ui/SvgFile'
import SvgFillSync from '@jotta/ui/SvgFillSync'
import SvgFolderOpen from '@jotta/ui/SvgFolderOpen'
import SvgFunnelSimple from '@jotta/ui/SvgFunnelSimple'
import SvgWarningCircle from '@jotta/ui/SvgWarningCircle'
import SvgX from '@jotta/ui/SvgX'
import { useHideFab } from '@jotta/ui/useHideFab'
import { cn } from '@jotta/utils/css'
import { filesize } from '@jotta/utils/filesize'

import { type UploadSummary, useUploadStore } from '../store/useUploadStore'
import type { UploadFilter, UploadItem } from '../types'

const UPLOAD_ROW_ESTIMATE_PX = 64

function isOngoingUploadItem(item: UploadItem) {
  return (
    item.status === 'QUEUED' ||
    item.status === 'PREPARING' ||
    item.status === 'UPLOADING'
  )
}

function formatSpeed(bytesPerSecond: number) {
  if (!bytesPerSecond) {
    return null
  }

  return `${filesize(bytesPerSecond, { round: 0 })}/s`
}

function formatUploadSpeed(bytesPerSecond: number) {
  return formatSpeed(bytesPerSecond) ?? '0 KB/s'
}

function formatRemainingTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return t`Less than a minute remaining`
  }

  const count = Math.ceil(seconds / 60)
  return plural(count, {
    one: '# minute remaining',
    other: '# minutes remaining',
  })
}

function getUploadPhaseProgress(item: UploadItem) {
  if (item.fileBytes <= 0) {
    return 0
  }

  const completed =
    item.status === 'PREPARING' ? item.hashBytes : item.uploadBytes

  return Math.min(1, completed / item.fileBytes)
}

function getUploadStatusText(item: UploadItem) {
  const uploadSize = filesize(item.uploadBytes, { round: 0 })
  const fileSize = filesize(item.fileBytes, { round: 0 })
  const speed = formatUploadSpeed(item.speedBytesPerSecond)

  switch (item.status) {
    case 'SUCCESS':
      return t`Done`
    case 'ERROR':
      return t`Failed to upload ${fileSize}`
    case 'QUEUED':
    case 'PREPARING':
      return t`Preparing to upload ${fileSize}`
    case 'UPLOADING':
      return t`${uploadSize} of ${fileSize} (${speed})`
    default:
      return ''
  }
}

function getOverallTitle(summary: UploadSummary) {
  const { counts } = summary

  if (!counts.ongoing && counts.failed === 0 && counts.all > 0) {
    return t`Uploads are complete`
  }

  if (counts.failed > 0) {
    return t`Uploads failed`
  }

  return plural(counts.all, {
    one: 'Upload',
    other: 'Uploads',
  })
}

function getOverallSubtitle(summary: UploadSummary) {
  const { counts } = summary

  if (counts.failed > 0) {
    return plural(counts.failed, {
      one: '# file failed to upload',
      other: '# files failed to upload',
    })
  }

  if (!counts.ongoing) {
    return plural(counts.completed, {
      one: '# file successfully uploaded',
      other: '# files successfully uploaded',
    })
  }

  if (!summary.uploadingSpeedBytesPerSecond) {
    if (summary.uploadingCount > 0) {
      return t`Uploading`
    }

    return t`Preparing uploads`
  }

  return formatRemainingTime(
    summary.remainingUploadBytes / summary.uploadingSpeedBytesPerSecond,
  )
}

function getFilterLabel(
  filter: UploadFilter,
  counts: Record<UploadFilter, number>,
) {
  switch (filter) {
    case 'all':
      return t`All uploads`
    case 'ongoing':
      return counts.ongoing > 0 ? t`Ongoing (${counts.ongoing})` : t`Ongoing`
    case 'completed':
      return counts.completed > 0
        ? t`Completed (${counts.completed})`
        : t`Completed`
    case 'failed':
      return counts.failed > 0 ? t`Failed (${counts.failed})` : t`Failed`
  }
}

function UploadFilterMenu({
  current,
  onSelect,
  counts,
}: {
  current: UploadFilter
  onSelect: (filter: UploadFilter) => void
  counts: Record<UploadFilter, number>
}) {
  const disabledFilters: Record<UploadFilter, boolean> = {
    all: counts.all === 0,
    ongoing: counts.ongoing === 0,
    completed: counts.completed === 0,
    failed: counts.failed === 0,
  }
  const isFiltered = current !== 'all'

  return (
    <div className="flex items-center gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="btn btn-tonal" aria-label={t`Filter uploads`}>
            <SvgFunnelSimple />
            {isFiltered && (
              <div className="absolute -right-1 top-0 h-4 w-4 rounded-full border-2 border-surface-container-lowest bg-on-primary-container" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="tabular-nums">
          {(['all', 'ongoing', 'completed', 'failed'] as UploadFilter[]).map(
            filter => (
              <DropdownMenuItem
                key={filter}
                disabled={disabledFilters[filter]}
                onSelect={() => onSelect(filter)}
                className={cn(
                  current === filter
                    ? 'text-on-primary-container focus:text-on-primary-container'
                    : 'text-primary focus:text-primary',
                )}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  {current === filter && (
                    <SvgBoldCheck
                      className="text-on-primary-container"
                      width={16}
                    />
                  )}
                </span>
                {getFilterLabel(filter, counts)}
              </DropdownMenuItem>
            ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function UploadRow({
  item,
  isLast,
  style,
  onCancel,
  onOpenItem,
  onToggleError,
}: {
  item: UploadItem
  isLast: boolean
  style: CSSProperties
  onCancel: () => void
  onOpenItem: () => void
  onToggleError: () => void
}) {
  const nameId = useId()
  const statusId = useId()
  const progressId = useId()
  const statusText = getUploadStatusText(item)
  const progress = getUploadPhaseProgress(item)
  const progressPercent = Math.round(progress * 100)
  const descriptionIds =
    item.status === 'UPLOADING' ? `${statusId} ${progressId}` : statusId
  const runAction = useCallback(
    (action: () => void, event: MouseEvent<HTMLButtonElement>) => {
      action()
      if (event.detail > 0) {
        event.currentTarget.blur()
      }
    },
    [],
  )
  const hasHoverActions =
    (item.status === 'SUCCESS' && Boolean(item.openItem)) ||
    item.status === 'ERROR' ||
    isOngoingUploadItem(item)

  return (
    <li
      style={style}
      aria-labelledby={nameId}
      aria-describedby={descriptionIds}
      className={cn(
        'absolute left-0 top-0 w-full px-2 py-1',
        !isLast &&
          'after:absolute after:bottom-0 after:left-3 after:right-3 after:border-b after:border-outline/20',
      )}
    >
      <div className="group relative my-0.5 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors focus-within:bg-surface-container-low hover:bg-surface-container-low">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-container-low text-on-surface/60"
          aria-hidden="true"
        >
          <SvgFile height={18} width={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p
            id={nameId}
            className="truncate text-base font-semibold leading-tight text-on-surface"
          >
            {item.name}
          </p>
          <p
            id={statusId}
            className={`truncate text-sm leading-tight ${
              item.status === 'ERROR' ? 'text-error' : 'text-on-surface/60'
            }`}
          >
            {statusText}
          </p>
          {item.status === 'UPLOADING' && (
            <span id={progressId} className="sr-only">
              {t`Upload progress ${progressPercent}%`}
            </span>
          )}
        </div>

        <div
          className={`ml-2 flex min-h-9 w-9 shrink-0 items-center justify-center transition-opacity ${
            hasHoverActions
              ? 'group-focus-within:opacity-0 group-hover:opacity-0'
              : ''
          }`}
          aria-hidden="true"
        >
          {item.status === 'SUCCESS' && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full text-on-surface">
              <SvgFolderOpen />
            </div>
          )}
          {item.status === 'ERROR' && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full text-error">
              <SvgWarningCircle />
            </div>
          )}
          {(item.status === 'QUEUED' || item.status === 'PREPARING') && (
            <div className="flex h-8 w-8 items-center justify-center text-on-surface/40">
              <Spinner
                primaryStrokeClassName="stroke-outline"
                secondaryStrokeClassName="stroke-outline-light"
                size="small"
              />
            </div>
          )}
          {item.status === 'UPLOADING' && (
            <div className="h-8 w-8 text-informative">
              <CircularProgress
                primaryStrokeClassName="stroke-m3-primary"
                secondaryStrokeClassName="stroke-primary-container"
                progress={progress}
                size="medium"
              />
            </div>
          )}
        </div>

        {hasHoverActions && (
          <div className="pointer-events-none absolute inset-y-0 right-2 z-10 flex items-center gap-2 rounded-lg opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            {item.status === 'SUCCESS' && item.openItem && (
              <button
                className="btn btn-elevated pointer-events-auto"
                aria-label={t`Open ${item.name}`}
                aria-describedby={descriptionIds}
                onClick={event => runAction(onOpenItem, event)}
              >
                <SvgFolderOpen aria-hidden="true" />
                <Trans>Open</Trans>
              </button>
            )}

            {item.status === 'ERROR' && (
              <button
                className="btn btn-elevated pointer-events-auto"
                aria-label={t`Show upload error for ${item.name}`}
                aria-describedby={descriptionIds}
                onClick={event => runAction(onToggleError, event)}
              >
                <SvgWarningCircle aria-hidden="true" />{' '}
                <Trans>What happened?</Trans>
              </button>
            )}

            {isOngoingUploadItem(item) && (
              <button
                className="btn btn-elevated pointer-events-auto h-9 min-h-9 px-2.5"
                aria-label={t`Cancel upload for ${item.name}`}
                aria-describedby={descriptionIds}
                onClick={event => runAction(onCancel, event)}
              >
                <Trans>Cancel</Trans>
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

function UploadList({
  activeFilter,
  enabled,
  onCancel,
  onOpenItem,
  onToggleError,
}: {
  activeFilter: UploadFilter
  enabled: boolean
  onCancel: (id: string) => void
  onOpenItem: (item: UploadItem) => void
  onToggleError: (id: string) => void
}) {
  const items = useUploadStore(state => state.items)
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  )
  const setScrollParentRef = useCallback((node: HTMLDivElement | null) => {
    setScrollElement(node)
  }, [])

  const filteredItems = useMemo(() => {
    switch (activeFilter) {
      case 'ongoing':
        return items.filter(isOngoingUploadItem)
      case 'completed':
        return items.filter(item => item.status === 'SUCCESS')
      case 'failed':
        return items.filter(item => item.status === 'ERROR')
      case 'all':
      default:
        return items
    }
  }, [activeFilter, items])

  const rowVirtualizer = useVirtualizer({
    count: filteredItems.length,
    enabled,
    getScrollElement: () => scrollElement,
    estimateSize: () => UPLOAD_ROW_ESTIMATE_PX,
    overscan: 8,
    getItemKey: index => filteredItems[index]?.id ?? index,
  })
  const virtualRows = rowVirtualizer.getVirtualItems()

  return (
    <div
      ref={setScrollParentRef}
      className="max-h-[min(44vh,22rem)] overflow-y-auto overflow-x-hidden md:max-h-[min(50vh,28rem)]"
    >
      <ul
        aria-label={t`Uploads`}
        className="relative w-full"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {virtualRows.map(virtualRow => {
          const item = filteredItems[virtualRow.index]
          if (!item) {
            return null
          }

          return (
            <UploadRow
              key={virtualRow.key}
              item={item}
              isLast={virtualRow.index === filteredItems.length - 1}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
              onCancel={() => onCancel(item.id)}
              onOpenItem={() => onOpenItem(item)}
              onToggleError={() => onToggleError(item.id)}
            />
          )
        })}
      </ul>
    </div>
  )
}

function hasActiveModalDialog() {
  if (typeof document === 'undefined') {
    return false
  }

  return Boolean(
    document.querySelector(
      '[data-testid="RadixModalOverlay"], [data-slot="alert-dialog-overlay"], [aria-modal="true"]:not([data-upload-panel-dialog="true"])',
    ),
  )
}

function useHasActiveModalDialog(panelRef: RefObject<HTMLElement | null>) {
  const [hasActiveModal, setHasActiveModal] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const update = () => {
      setHasActiveModal(hasActiveModalDialog())
    }

    update()

    const observer = new MutationObserver(mutations => {
      const panel = panelRef.current

      if (
        panel &&
        mutations.every(
          mutation =>
            mutation.target instanceof Node && panel.contains(mutation.target),
        )
      ) {
        return
      }

      update()
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-modal', 'class', 'data-state'],
    })

    return () => {
      observer.disconnect()
    }
  }, [panelRef])

  return hasActiveModal
}

export function FileUploadPanel() {
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [keepExpandedShell, setKeepExpandedShell] = useState(false)
  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null)
  const panelElementRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const subtitleId = useId()
  const isModalDialogActive = useHasActiveModalDialog(panelElementRef)
  const setPanelElement = useCallback((node: HTMLElement | null) => {
    panelElementRef.current = node
  }, [])

  const cancelUpload = useUploadStore(state => state.cancelUpload)
  const clearUploads = useUploadStore(state => state.clearUploads)
  const filter = useUploadStore(state => state.filter)
  const summary = useUploadStore(state => state.summary)
  const retryFailed = useUploadStore(state => state.retryFailed)
  const retryUpload = useUploadStore(state => state.retryUpload)
  const setFilter = useUploadStore(state => state.setFilter)
  const selectedError = useUploadStore(state => {
    if (selectedErrorId == null) {
      return undefined
    }

    return state.items.find(
      item => item.id === selectedErrorId && item.status === 'ERROR',
    )
  })

  const isVisible = summary.counts.all > 0
  useHideFab(isVisible)
  const showExpandedShell = expanded || keepExpandedShell
  const overallTitle = getOverallTitle(summary)
  const overallSubtitle = getOverallSubtitle(summary)
  const filterCounts = summary.counts
  const activeFilter =
    filter === 'all' || filterCounts[filter] > 0 ? filter : 'all'

  const progress =
    summary.totalBytes > 0 ? summary.completedBytes / summary.totalBytes : 0
  const successCount = filterCounts.completed
  const errorCount = filterCounts.failed
  const pendingCount = filterCounts.ongoing
  const hasPendingItems = pendingCount > 0
  const hasSuccesses = successCount > 0
  const hasErrorsInItems = errorCount > 0
  const allItemsProcessed = !hasPendingItems
  const progressForRing = allItemsProcessed ? 1 : progress
  const totalSpeed = summary.uploadingSpeedBytesPerSecond
  const hasStartedTransfer =
    hasPendingItems && (summary.uploadingCount > 0 || successCount > 0)

  const progressPrimaryStrokeClassName = hasErrorsInItems
    ? allItemsProcessed
      ? 'stroke-error'
      : hasSuccesses
        ? 'stroke-m3-primary'
        : 'stroke-error'
    : progress === 1
      ? 'stroke-good'
      : 'stroke-m3-primary'

  const progressSecondaryStrokeClassName =
    hasErrorsInItems && hasPendingItems && hasSuccesses
      ? 'stroke-error'
      : 'stroke-primary-container'

  useEffect(() => {
    if (isModalDialogActive) {
      // Avoid stacking cancel-confirm on top of conflict / other modals.
      setConfirmCloseOpen(false)
      setExpanded(false)
      setKeepExpandedShell(false)
    }
  }, [isModalDialogActive])

  useEffect(() => {
    if (!expanded && selectedErrorId != null) {
      setSelectedErrorId(null)
    }
  }, [expanded, selectedErrorId])

  useEffect(() => {
    if (confirmCloseOpen && !hasPendingItems) {
      setConfirmCloseOpen(false)
    }
  }, [confirmCloseOpen, hasPendingItems])

  useEffect(() => {
    if (summary.counts.all === 0) {
      setConfirmCloseOpen(false)
      setExpanded(false)
      setKeepExpandedShell(false)
      setSelectedErrorId(null)
    }
  }, [summary.counts.all])

  useEffect(() => {
    if (expanded && !keepExpandedShell) {
      setKeepExpandedShell(true)
    }
  }, [expanded, keepExpandedShell])

  const handleToggleExpanded = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      setExpanded(value => !value)
    },
    [],
  )

  const handleDismiss = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (hasPendingItems) {
        if (isModalDialogActive) {
          return
        }
        setConfirmCloseOpen(true)
        return
      }
      clearUploads()
    },
    [clearUploads, hasPendingItems, isModalDialogActive],
  )

  const handleConfirmCancelUploads = useCallback(() => {
    setConfirmCloseOpen(false)
    clearUploads()
  }, [clearUploads])

  const handleOpenItem = useCallback((item: UploadItem) => {
    setExpanded(false)
    item.openItem?.(item.filePath)
  }, [])

  const handleToggleError = useCallback((id: string) => {
    setSelectedErrorId(value => (value === id ? null : id))
  }, [])

  if (!isVisible) {
    return null
  }

  const panelClass = cn(
    'fixed bottom-3 left-1/2 top-auto grid max-h-[calc(100vh-1rem)] w-[calc(100%-1rem)] max-w-[400px] -translate-x-1/2 translate-y-0 overflow-hidden rounded-[24px] bg-surface-container-lowest pb-2 pt-3 tabular-nums shadow-[0_5px_24px_rgba(0,0,0,0.15)] outline-none sm:w-[calc(100%-2rem)] md:bottom-3 md:left-auto md:right-3 md:top-auto md:max-h-[min(48rem,calc(100vh-3rem))] md:translate-x-0 md:translate-y-0',
    !expanded && !keepExpandedShell && 'cursor-pointer',
    selectedError && 'min-h-[min(19rem)]',
  )
  const panelContent = (
    <>
      {!expanded && (
        <button
          className="absolute inset-0 z-10"
          aria-label={t`Expand uploads`}
          onClick={() => setExpanded(true)}
        />
      )}
      <div
        className={cn(
          'relative flex min-w-0 items-center gap-3 pb-1 pl-4 pr-2',
          {
            'pb-3': expanded,
          },
        )}
      >
        <div className="shrink-0">
          <CircularProgress
            primaryStrokeClassName={progressPrimaryStrokeClassName}
            secondaryStrokeClassName={progressSecondaryStrokeClassName}
            progress={progressForRing}
            size="medium"
          >
            {hasErrorsInItems && allItemsProcessed ? (
              <SvgExplamationMark className="text-error" width={24} />
            ) : (
              progress === 1 && (
                <SvgBoldCheck className="text-good" width={16} />
              )
            )}
          </CircularProgress>
        </div>
        <div className="min-w-0 flex-1">
          <p
            id={titleId}
            className="label-md break-words leading-5 text-on-surface"
          >
            {overallTitle}
          </p>
          <p
            id={subtitleId}
            aria-live={isModalDialogActive ? 'off' : 'polite'}
            aria-atomic="true"
            className="body-sm-sh break-words text-on-surface/60"
          >
            {overallSubtitle}
          </p>
        </div>
        <button
          className="btn btn-ghost relative z-20 shrink-0"
          aria-label={expanded ? t`Collapse uploads` : t`Expand uploads`}
          onClick={handleToggleExpanded}
        >
          {expanded ? <SvgCaretDown /> : <SvgCaretUp />}
        </button>
        <button
          className="btn btn-ghost relative z-20 shrink-0"
          aria-label={t`Dismiss uploads panel`}
          onClick={handleDismiss}
        >
          <SvgX />
        </button>
      </div>

      {showExpandedShell && (
        <motion.div
          initial={expanded ? { height: 0, opacity: 0 } : false}
          animate={
            expanded
              ? { height: 'auto', opacity: 1 }
              : { height: 0, opacity: 0 }
          }
          transition={{ duration: 0.22, ease: 'easeInOut' }}
          onAnimationComplete={() => {
            if (!expanded) {
              setKeepExpandedShell(false)
            }
          }}
          className="w-full overflow-hidden"
        >
          <UploadList
            activeFilter={activeFilter}
            enabled={expanded}
            onCancel={cancelUpload}
            onOpenItem={handleOpenItem}
            onToggleError={handleToggleError}
          />

          <div className="flex items-center justify-between border-t border-outline/20 px-2 pt-2">
            <UploadFilterMenu
              current={activeFilter}
              counts={filterCounts}
              onSelect={setFilter}
            />
            {hasErrorsInItems && allItemsProcessed ? (
              <button
                className="btn btn-tonal flex"
                aria-label={t`Retry all failed uploads`}
                onClick={retryFailed}
              >
                <Trans>
                  <SvgFillSync />
                  Retry
                </Trans>
              </button>
            ) : (
              <p className="pr-2 text-base text-on-surface/80">
                {hasStartedTransfer
                  ? `↑ ${formatUploadSpeed(totalSpeed)}`
                  : ' '}
              </p>
            )}
          </div>

          <Dialog
            open={Boolean(selectedError)}
            onOpenChange={nextOpen => {
              if (!nextOpen) {
                setSelectedErrorId(null)
              }
            }}
          >
            <DialogPortal container={panelElementRef.current ?? undefined}>
              <DialogOverlay className="absolute inset-0 z-20 bg-on-surface/30" />
              {selectedError && (
                <DialogPrimitive.Content
                  className="absolute bottom-2 left-2 right-2 z-30 max-h-[calc(100%-1rem)] w-auto max-w-none translate-x-0 translate-y-0 rounded-2xl bg-surface-container-lowest p-4 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:bottom-3 sm:left-3 sm:right-3 sm:max-w-none"
                  onEscapeKeyDown={event => {
                    event.preventDefault()
                    setSelectedErrorId(null)
                  }}
                  onPointerDownOutside={() => {
                    setSelectedErrorId(null)
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <DialogPrimitive.Title asChild>
                        <p className="truncate text-base font-semibold text-on-surface">
                          {selectedError.name}
                        </p>
                      </DialogPrimitive.Title>
                      <p className="mt-1 text-base text-red-600">
                        {getUploadStatusText(selectedError)}
                      </p>
                    </div>
                    <button
                      className="btn btn-ghost h-9 min-h-9 w-9 p-0"
                      aria-label={t`Close upload error details`}
                      onClick={() => setSelectedErrorId(null)}
                    >
                      <SvgX />
                    </button>
                  </div>

                  <div className="mt-4 rounded-xl bg-surface-container p-3">
                    <DialogPrimitive.Description asChild>
                      <div className="flex w-full justify-between gap-2 text-base font-semibold text-on-surface/80">
                        <p>{t`Error uploading file`}</p>
                        {selectedError.error?.message && (
                          <CopyButton
                            value={selectedError.error.message}
                            className="btn btn-icon bg-surface-container-lowest text-on-surface"
                          />
                        )}
                      </div>
                    </DialogPrimitive.Description>
                    {selectedError.error?.message && (
                      <p className="mt-2 text-base text-on-surface/80">
                        {selectedError.error.message}
                      </p>
                    )}
                    <p className="mt-2 text-base text-on-surface/80">
                      <span className="font-semibold">XID: </span>{' '}
                      {selectedError.error?.xid || selectedError.id}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-2">
                    <button
                      className="btn btn-tonal flex"
                      aria-label={t`Retry upload`}
                      onClick={() => {
                        retryUpload(selectedError.id)
                        setSelectedErrorId(null)
                      }}
                    >
                      <Trans>
                        <SvgFillSync />
                        Retry
                      </Trans>
                    </button>
                  </div>
                </DialogPrimitive.Content>
              )}
            </DialogPortal>
          </Dialog>
        </motion.div>
      )}

      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent
          data-upload-panel-dialog="true"
          className="max-w-[calc(100%-2rem)] sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              <Trans>Uploads in progress</Trans>
            </DialogTitle>
            <DialogDescription className="whitespace-normal text-base text-on-surface/80">
              <Trans>
                Are you sure you want to cancel the ongoing uploads?
              </Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              className="btn btn-primary"
              onClick={() => setConfirmCloseOpen(false)}
            >
              <Trans>Keep uploading</Trans>
            </button>
            <button
              className="btn btn-elevated"
              onClick={handleConfirmCancelUploads}
            >
              <Trans>Cancel uploads</Trans>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  return (
    <Portal>
      {showExpandedShell && !isModalDialogActive ? (
        <DialogPrimitive.Root modal={false} open>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Content
              ref={setPanelElement}
              data-upload-panel-dialog="true"
              aria-labelledby={titleId}
              aria-describedby={subtitleId}
              className={panelClass}
              onEscapeKeyDown={event => {
                event.preventDefault()
                if (selectedErrorId != null) {
                  setSelectedErrorId(null)
                  return
                }
                setExpanded(false)
              }}
            >
              <DialogPrimitive.Title className="sr-only">
                {overallTitle}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                {overallSubtitle}
              </DialogPrimitive.Description>
              {panelContent}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : (
        <section
          ref={setPanelElement}
          aria-labelledby={titleId}
          aria-describedby={subtitleId}
          aria-hidden={isModalDialogActive}
          className={panelClass}
        >
          {panelContent}
        </section>
      )}
    </Portal>
  )
}
